import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { Frac } from '~lmo/models/frac.model';
import { LmoFracsService, LmoReportsService } from '~lmo/services';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';

interface ReportFormValue {
  available: boolean;
  description: string;
  eta: number;
  siteId: number;
}

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-well-status-report',
  styleUrls: ['./lmo-well-status-report.component.scss'],
  templateUrl: './lmo-well-status-report.component.html',
})
export class LmoWellStatusReportComponent implements OnInit {
  public frac$: Observable<Frac>;
  public reportForm: FormGroup;
  public isLoading = false;
  constructor(
    private fracService: LmoFracsService,
    private reportService: LmoReportsService,
    private fb: FormBuilder,
    private lmoModal: LmoFracModalService,
  ) {}

  public ngOnInit() {
    this.frac$ = this.fracService.currentFrac$;
    this.reportForm = this.fb.group({
      available: [false, [Validators.required]],
      description: '',
      eta: null,
      sendToReload: false,
      siteId: null,
    });
    this.toggleDownMode();
    this.reportForm.get('available').valueChanges.subscribe((available) => {
      if (available) {
        this.toggleUpMode();
      } else {
        this.toggleDownMode();
      }
    });

    this.frac$
      .pipe(
        filter((frac) => !!frac),
        switchMap((frac) => this.reportService.getReportBySiteId$(frac.site.id)),
        take(1),
      )
      .subscribe((report) => {
        this.reportForm.get('available').setValue(!report.available);
      });
    this.frac$
      .pipe(
        filter((frac) => !!frac),
        take(1),
      )
      .subscribe((frac) => {
        this.reportForm.get('siteId').setValue(frac.site.id);
      });
  }

  public createReport() {
    this.isLoading = true;
    if (this.reportForm.invalid) {
      return;
    }
    const formValue: ReportFormValue = this.reportForm.value;
    this.reportService
      .createSiteReport$({
        available: formValue.available,
        description: formValue.description,
        eta: formValue.eta * 60 /* send in minutes */,
        sendToReload: false,
        siteId: formValue.siteId,
      })
      .subscribe(() => {
        this.isLoading = false;
        this.lmoModal.closeHomeModal();
      });
  }

  private toggleDownMode() {
    const descriptionControl = this.reportForm.get('description') as FormControl;
    descriptionControl.enable();

    const etaControl = this.reportForm.get('eta') as FormControl;
    etaControl.enable();
    etaControl.setValidators(Validators.required);
  }

  private toggleUpMode() {
    const descriptionControl = this.reportForm.get('description') as FormControl;
    descriptionControl.disable();

    const etaControl = this.reportForm.get('eta') as FormControl;
    etaControl.disable();
    etaControl.clearValidators();
  }
}
